import { HUB_CHANGE_KINDS, HubChangeItem, HubChangeKind } from '../../types/hubChanges';
import { Card } from '../../types/card';
import { isDrop, isRise } from '../../constants/constants';
import dayjs from 'dayjs';

/**
 * "매장 가격 요청" 표의 한 줄 — 같은 카드의 여러 항목(예: 상승 + 확정 대기 알림)을 하나로 합친 것.
 * 대표 종류(kind)는 notice > rise > drop > hub > store 순으로 고르고, 나머지는 kinds 에 함께 둔다.
 */
export interface HubRow extends HubChangeItem {
  /** 표 rowKey — 카드가 있으면 card:<id>, 없으면 code:<상품코드>. */
  key:   string;
  /** 이 줄이 가진 종류 전부(대표 kind 포함, 우선순위 순). */
  kinds: HubChangeKind[];
  /**
   * 네이버 재고 수량(감사 card-controltower#348) — hub-changes 응답에는 없고 CT 카드 목록(/api/cards — 스토어 상품 관리와 같은 출처)
   * 에서 덮는다(overlayLocalCards). CT 에 없는 상품 · 카드 목록을 아직 못 받았으면 없음.
   */
  stockQuantity?: number | null;
  /** 카드 이미지(감사 card-controltower#439) — 재고처럼 CT 카드 목록에서 덮는다. 없으면 자리표시. */
  imageUrl?: string | null;
  /** 한 줄 안내(rowAdvice)의 재료 — CT 카드 목록에서 덮는다(overlayLocalCards). 하락 분류 · 타 샵 최저 · 최고가 · 가격 지킴. 없으면 모름. */
  dropBucket?: string | null;
  marketMin?: number | null;
  marketMax?: number | null;
  priceHeld?: boolean;
}

const KIND_PRIORITY: HubChangeKind[] = ['notice', 'rise', 'drop', 'hub', 'store'];
const rank = (k: HubChangeKind) => KIND_PRIORITY.indexOf(k);

const isKind = (v: unknown): v is HubChangeKind =>
  typeof v === 'string' && (HUB_CHANGE_KINDS as string[]).includes(v);

const rowKeyOf = (i: HubChangeItem) =>
  i.cardId != null ? `card:${i.cardId}` : `code:${i.sellerProductCode}`;

/**
 * 응답 항목을 카드 기준으로 중복 없이 합친다. 백엔드(PR #43 최종)는 이미 카드당 한 줄(대표 kind + 나머지
 * flags)로 주지만, 같은 cardId 가 두 줄 와도 하나로 보이게 방어한다(2026-10-21 팀장 지시). 종류 판단 · 건수는
 * 대표 kind 와 flags 를 합친 kinds 로 한다 — 서버 counts 는 대표 kind 기준이라 쓰지 않는다. 순서는 처음 나온 순(백엔드 정렬 notice → rise → drop → hub → store)을 따른다.
 */
export function mergeHubItems(items: HubChangeItem[]): HubRow[] {
  const byKey = new Map<string, HubChangeItem[]>();
  for (const it of items) {
    const k = rowKeyOf(it);
    const list = byKey.get(k);
    if (list) list.push(it); else byKey.set(k, [it]);
  }
  const rows: HubRow[] = [];
  byKey.forEach((group, key) => {
    const sorted = [...group].sort((a, b) => rank(a.kind) - rank(b.kind));
    const primary = sorted[0];
    const kindSet = new Set<HubChangeKind>();
    sorted.forEach((g) => {
      kindSet.add(g.kind);
      (g.flags ?? []).forEach((f) => { if (isKind(f)) kindSet.add(f); });
    });
    // 알림 정보(noticeId · 보고 값)는 notice 항목에서, 출처 · 시각은 대표 항목에서 가져온다.
    const notice = sorted.find((g) => g.kind === 'notice');
    rows.push({
      ...primary,
      noticeId:       notice?.noticeId ?? primary.noticeId,
      manualOverride: sorted.some((g) => g.manualOverride),
      canApplyCurrent: sorted.some((g) => g.canApplyCurrent === true),
      changedSince:   sorted.some((g) => g.changedSince),
      // 요청 대기 · 고정 · 서버 사유(백엔드 PR #47) — 같은 카드의 어느 줄에든 있으면 그 카드의 것이다.
      pendingRequest: sorted.find((g) => g.pendingRequest)?.pendingRequest ?? null,
      locked:         sorted.some((g) => g.locked === true),
      applyCurrentBlockReason: sorted.find((g) => g.applyCurrentBlockReason)?.applyCurrentBlockReason ?? null,
      key,
      kinds: Array.from(kindSet).sort((a, b) => rank(a) - rank(b)),
    });
  });
  return rows;
}


export const HUB_NO_CARD_TOOLTIP = 'CT 에 없는 상품이라 반영 · 요청할 수 없습니다';

/**
 * PriceHub 관리자가 아직 확정 · 무시하지 않은 요청이 있는가 — 요청 대기(apply_current · manual_request · decided · detected …).
 * 규칙 제안(auto_rule)은 들지 않는다: 백엔드 SYNC_NAVER 가 빼는 집합(요청 대기 · 확정 대기 중 auto_rule 제외)과 같아야
 * "확인 중인데 ③ 에 나감" · "할 일인데 ③ 이 건너뜀" 이 생기지 않는다(감사 card-controltower#71, 설계 2026-11-04 §1 · §7-1).
 */
export const isHubRequestPending = (r: Pick<HubRow, 'pendingRequest'>): boolean =>
  !!r.pendingRequest && r.pendingRequest.kind !== 'auto_rule';

/**
 * 'PriceHub 확인 중'(용어 사전 2026-11-04) — 요청 대기(규칙 제안 제외) 또는 열린 직접 변경 감지(manualOverride). 그동안 CT 는 그 카드를
 * 반영 · 요청하지 않는다. 예전 isHubRowBlocked 는 알림이면 규칙 제안까지 막았다 — 지금은 ③ 과 같은 정의.
 */
export const isHubChecking = (r: Pick<HubRow, 'pendingRequest' | 'manualOverride'>): boolean =>
  isHubRequestPending(r) || r.manualOverride;

/**
 * "네이버 판매가 → PriceHub 가격" 요청(apply-current)을 보낼 수 없는 사유(보낼 수 있으면 undefined). 서버가 사유(applyCurrentBlockReason —
 * "요청 대기 중 …" · "고정됨 …")를 주면 그 문구를 그대로 쓰고, 예전 응답(사유 칸 없음)은 프런트 문구로.
 */
export function applyCurrentBlockReason(r: HubRow): string | undefined {
  if (r.cardId == null) return HUB_NO_CARD_TOOLTIP;
  if (!r.canApplyCurrent && r.applyCurrentBlockReason) return r.applyCurrentBlockReason;
  if (r.currentPrice == null || r.currentPrice <= 0) return '네이버 판매가가 없습니다(0원 또는 미조회) — 재고 동기화 뒤 다시 하세요';
  if (!r.canApplyCurrent) {
    return 'PriceHub 가격을 요청할 수 없는 카드입니다(상품코드 없음 · 대상이 아닌 게임 · PriceHub 가격 조회 꺼짐)';
  }
  return undefined;
}

/**
 * 일괄 작업 바 · 미리보기의 제외 사유 짧은 이름(감사 card-controltower#696) — 같은 사유끼리 묶어 '제외 3 (네이버 판매가 없음 2 · …)'
 * 로 센다. 긴 문장은 줄 글(applyCurrentBlockReason)에 그대로 남는다. 서버 사유는 첫 마디('—' · '(' 앞)만 쓴다.
 */
export function applyCurrentExcludeLabel(r: HubRow): string | undefined {
  if (!applyCurrentBlockReason(r)) return undefined;
  if (r.cardId == null) return 'CT 에 없음';
  if (!r.canApplyCurrent && r.applyCurrentBlockReason) {
    const head = r.applyCurrentBlockReason.split(/\s*[—(]/)[0].trim();
    return head.length > 0 && head.length <= 24 ? head : '보낼 수 없음';
  }
  if (r.currentPrice == null || r.currentPrice <= 0) return '네이버 판매가 없음';
  return '요청 대상 아님';
}


/**
 * 받은 뒤 화면에서 바뀐 카드 값을 줄에 덮는다(2026-10-21 코덱 리뷰 #3) — 하락 검토에서 적용가를 고치거나
 * 현재가 유지를 저장하면 목록을 다시 받지 않아도 가격 · 상승/하락 종류가 지금 카드와 같아야 반영 기준과
 * 화면이 어긋나지 않는다. 상승 · 하락은 로컬 비교라 카드의 priceChangeStatus 로 다시 정하고, 둘 다 아니게 됐는데
 * 다른 종류(알림 · 공통가 · 매장가)도 없으면 줄을 뺀다 — 단 PriceHub 확인 중인 요청이 걸린 줄은 남긴다(확인 중 칩에).
 * 빼면 상단 요약(서버 pendingRequests)은 '확정 대기 2건' 인데 탭은 1 이라 요청 하나가 어느 탭에도 안 보였다(시우 검수 2026-10-30, 감사 #517).
 * 한 줄 안내(rowAdvice)의 재료 — 하락 분류 · 타 샵 시세 · 가격 지킴(priceHeld) — 도 카드에서 덮는다(설계 2026-11-04 §2, B-d 없이 프런트 overlay).
 */
export function overlayLocalCards(rows: HubRow[], cardsById: Map<number, Card>): HubRow[] {
  if (cardsById.size === 0) return rows;
  const out: HubRow[] = [];
  for (const r of rows) {
    const c = r.cardId != null ? cardsById.get(r.cardId) : undefined;
    if (!c) { out.push(r); continue; }
    const local: HubChangeKind | null =
      isRise(c.priceChangeStatus) ? 'rise' : isDrop(c.priceChangeStatus) ? 'drop' : null;
    const others = r.kinds.filter((k) => k !== 'rise' && k !== 'drop');
    const kinds = (local ? [...others, local] : others).sort((a, b) => rank(a) - rank(b));
    if (kinds.length === 0 && !isHubRequestPending(r)) continue;
    out.push({
      ...r,
      kind: kinds[0] ?? r.kind,
      kinds,
      currentPrice: c.currentPrice,
      suggestedPrice: c.suggestedPrice,
      // PriceHub 가 준 이 매장 값(수정가가 수동가로 덮여도 남는다 — 코덱 리뷰 #868). 옛 서버(칸 없음)는 받은 값 그대로.
      hubPrice: c.hubPrice ?? r.hubPrice,
      priceChangeRate: c.priceChangeRate,
      manualOverride: r.manualOverride || c.manualOverrideAt != null,
      stockQuantity: c.stockQuantity,
      imageUrl: c.imageUrl,
      dropBucket: c.dropBucket ?? null,
      marketMin: c.marketMin ?? null,
      marketMax: c.marketMax ?? null,
      priceHeld: c.priceHeld === true,
    });
  }
  return out;
}

export interface HubSelectionModel {
  rows:          HubRow[];
  /** 체크(요청)할 수 있는 카드 id — '할 일' 줄(hubRowBlockReason 이 없는 것). */
  selectableIds: number[];
  selectableSet: Set<number>;
}

/**
 * "매장 가격 요청" 표의 줄 · 체크 모델. 기본 선택은 없다(감사 card-controltower#330 — 선택은 사용자가 고른 것만).
 * 네이버 판매가 = PriceHub 가격인 줄은 CT 어디에도 보이지 않는다(사장 확정 2026-11-04 15:40 · 설계 §1 — 어떤 알림이 걸려 있어도) —
 * 여기서 빼므로 칩 수 · 전체 선택 · 묶음 요청 · 저장 칩 건수가 모두 같은 목록을 센다.
 */
export function buildHubSelection(
  items: HubChangeItem[],
  cardsById: Map<number, Card> = new Map(),
): HubSelectionModel {
  const rows = overlayLocalCards(mergeHubItems(items), cardsById).filter((r) => !isHubInSync(r));
  const selectableIds: number[] = [];
  for (const r of rows) {
    if (r.cardId == null) continue;
    if (hubRowBlockReason(r)) continue;
    selectableIds.push(r.cardId);
  }
  return { rows, selectableIds, selectableSet: new Set(selectableIds) };
}

/** 종류별 줄 수(한 줄이 여러 종류면 각각 센다). */
export function countHubKinds(rows: HubRow[]): Record<HubChangeKind, number> {
  const c: Record<HubChangeKind, number> = { rise: 0, drop: 0, hub: 0, store: 0, notice: 0 };
  rows.forEach((r) => r.kinds.forEach((k) => { c[k] += 1; }));
  return c;
}

// ─── 두 값 · 한 줄 안내(설계 2026-11-04 '작업자 기준 모델' §1 · §2) ─────────────────────────────────────────

/**
 * 'PriceHub 가격' — PriceHub 가 이 매장에 주는 기준값(매장 해석가). 상승 · 하락이 있으면 CT 가 받아 둔 PriceHub 값(hubPrice —
 * 수정가(suggestedPrice)는 사람이 덮을 수 있어 쓰지 않는다, 코덱 리뷰 #868), 공통 가격 · 매장 가격 변경이면 PriceHub 가 지금 주는 값(hubSellingPrice),
 * 알림만 있으면 PriceHub 가 지금 주는 값(hubSellingPrice — 백엔드가 알림 줄에도 지금 판매가를 싣는다), 없으면(옛 응답) 알림 전 값(oldPrice).
 * 어느 칸도 없는 옛 응답만 수정가로 떨어진다.
 */
export function hubStorePrice(r: HubRow): number | null {
  if (r.kinds.includes('rise') || r.kinds.includes('drop')) return r.hubPrice ?? r.suggestedPrice;
  if (r.kinds.includes('hub') || r.kinds.includes('store')) return r.hubSellingPrice ?? r.newPrice ?? r.hubPrice ?? r.suggestedPrice;
  if (r.kind === 'notice') return r.hubSellingPrice ?? r.oldPrice ?? r.hubPrice ?? r.suggestedPrice;
  return r.hubPrice ?? r.suggestedPrice;
}

/**
 * 수동 수정가 — CT 수정가(suggestedPrice)가 PriceHub 가격과 다르면(사람이 ② 에서 적용가를 고쳤거나 수동가로 덮음) 그 값. ③ 반영은 수정가를
 * 보내므로 안내 줄에 '수동 수정가 N원 있음' 으로 알린다(코덱 리뷰 #868). 추천 계산은 PriceHub 가격 기준 그대로. 상승 · 하락 줄만.
 */
export function hubManualPrice(r: HubRow): number | null {
  if (!r.kinds.includes('rise') && !r.kinds.includes('drop')) return null;
  const hub = hubStorePrice(r);
  return r.suggestedPrice != null && hub != null && r.suggestedPrice !== hub ? r.suggestedPrice : null;
}

/** 네이버 판매가와 PriceHub 가격이 둘 다 있고 같은가 — 같으면 그 줄은 CT 어디에도 보이지 않는다. */
export const isHubInSync = (r: HubRow): boolean => {
  const hub = hubStorePrice(r);
  return r.currentPrice != null && hub != null && r.currentPrice === hub;
};

/**
 * 안내 종류 — apply(반영 추천) · request(요청 추천) · check(확인 필요, 추천 없음) · fetchFirst(가격 받기 먼저) · checking(PriceHub 확인 중)
 * · excluded(대상 아님). 앞의 넷은 '할 일', checking 은 'PriceHub 확인 중' 칩, excluded 는 '대상 아님' 칩.
 */
export type RowAdviceKind = 'apply' | 'request' | 'check' | 'fetchFirst' | 'checking' | 'excluded';

/**
 * 안내 재료 — 매장 가격 요청 줄(hubRowFacts)과 가격 조회 줄(카드)이 같이 쓴다. 새 판정 기준은 없다: 하락 분류(② dropBucket)와 상승
 * 게이트(상한 maxRiseRate · 타 샵 최고가 — 백엔드 PriceReviewGate 와 같은 규칙)를 그대로 쓴다.
 */
export interface AdviceFacts {
  /** 네이버 판매가. */
  naver: number | null;
  /** PriceHub 가격. */
  hub: number | null;
  /** 대상 아님 사유(CT 에 없음 · 네이버 판매가 없음 · 고정 · 대상 아닌 게임 등) — 있으면 excluded. */
  excludedReason?: string | null;
  /** PriceHub 확인 중이면 그 글(⏳ 뒤에 붙일 말) — 있으면 checking. */
  checking?: string | null;
  /** 판매가를 사람이 정해 지키는 중(② '유지' · 판매가 결정 — priceHeld). */
  priceHeld?: boolean;
  /** PriceHub 가 방금 바뀌어 CT 가 아직 안 받음(가격 받기 먼저) — 바뀐 시각(ISO, 없으면 null). */
  fetchFirst?: { at: string | null } | null;
  /** 하락 분류(IN_RANGE · SUSPECT · NO_REFERENCE, 분류 전이면 null). */
  dropBucket?: string | null;
  /** 타 샵 최저 · 최고가(시세 분류 잡이 남긴 값). */
  marketMin?: number | null;
  marketMax?: number | null;
  /** 상승률 상한(기본 0.5 = 50% — 백엔드 price.auto-increase.max-rise-rate). */
  maxRiseRate?: number;
}

export interface RowAdvice {
  kind:   RowAdviceKind;
  /** 기준 값 쪽 — hub(반영: 네이버를 PriceHub 에 맞춤) · naver(요청: PriceHub 를 네이버에 맞춤). 추천이 없으면 null. */
  base:   'hub' | 'naver' | null;
  /** 바뀌는 값의 옛 값 → 새 값(반영이면 네이버, 요청이면 PriceHub). 바뀌는 값이 없으면 null. */
  from:   number | null;
  to:     number | null;
  /** 화면 글 그대로(설계 §2-2 예시 — 테스트 기준). */
  text:   string;
  /** 안내 뒤 꼬리 글(— 뒤) — 화면에서 두 줄로 나눠 그릴 때. */
  tail:   string;
}

export const DEFAULT_MAX_RISE_RATE = 0.5;
const won = (n: number) => n.toLocaleString('ko-KR');
const delta = (from: number, to: number) => (to > from ? `▲${won(to - from)}` : `▼${won(from - to)}`);
/** '+80%' · '−10%'(유니코드 빼기) — 네이버 판매가 기준으로 PriceHub 가 얼마나 다른가. */
export const ratePct = (naver: number, hub: number): string => {
  const p = Math.round(((hub - naver) / naver) * 100);
  return p >= 0 ? `+${p}%` : `−${Math.abs(p)}%`;
};

/**
 * 줄 하나의 안내(설계 2026-11-04 §2-1 판정 순서 — 위에서 처음 맞는 것 하나). 두 값이 같거나 하나라도 없으면(대상 아님 사유도 없으면) null.
 *  0 대상 아님 → 1 PriceHub 확인 중 → 2 가격 지킴(요청) → 3 가격 받기 먼저 → 4~6 내림(IN_RANGE 반영 · SUSPECT 요청 · 그 밖 확인 필요)
 *  → 7 올림(D1 안 A: 타 샵 최고가 초과 요청 · 상한 초과 확인 필요 · 그 밖 반영).
 */
export function rowAdvice(f: AdviceFacts): RowAdvice | null {
  const { naver, hub } = f;
  if (naver != null && hub != null && naver === hub) return null;
  if (f.excludedReason) return { kind: 'excluded', base: null, from: null, to: null, text: f.excludedReason, tail: f.excludedReason };
  if (f.checking) {
    const text = `⏳ PriceHub 확인 중 — ${f.checking}`;
    return { kind: 'checking', base: null, from: null, to: null, text, tail: f.checking };
  }
  if (naver == null || naver <= 0) {
    const t = '네이버 판매가가 없습니다';
    return { kind: 'excluded', base: null, from: null, to: null, text: t, tail: t };
  }
  if (hub == null || hub <= 0) {
    const t = 'PriceHub 가격이 없습니다 — PriceHub 에 가격을 넣어야 합니다';
    return { kind: 'excluded', base: null, from: null, to: null, text: t, tail: t };
  }
  const apply = (tail: string): RowAdvice => ({
    kind: 'apply', base: 'hub', from: naver, to: hub, tail,
    text: `PriceHub ${won(hub)} 기준 · 네이버 ${won(naver)} → ${won(hub)} ${delta(naver, hub)} — ${tail}`,
  });
  const request = (tail: string, withDelta = true): RowAdvice => ({
    kind: 'request', base: 'naver', from: hub, to: naver, tail,
    text: `네이버 ${won(naver)} 기준 · PriceHub ${won(hub)} → ${won(naver)}${withDelta ? ` ${delta(hub, naver)}` : ''} — ${tail}`,
  });
  const check = (tail: string): RowAdvice => ({
    kind: 'check', base: null, from: null, to: null, tail,
    text: `네이버 ${won(naver)} · PriceHub ${won(hub)} (${ratePct(naver, hub)}) — ${tail}`,
  });

  if (f.priceHeld) return request('네이버를 지키기로 했습니다, PriceHub 에 요청하면 끝납니다', false);
  if (f.fetchFirst) {
    const at = f.fetchFirst.at ? dayjs(f.fetchFirst.at) : null;
    const tail = '가격 받기 뒤 반영할 수 있습니다';
    const text = `PriceHub 가 ${won(hub)} 으로 바뀜${at && at.isValid() ? `(${at.format('HH:mm')})` : ''} — ${tail}`;
    return { kind: 'fetchFirst', base: 'hub', from: naver, to: hub, text, tail };
  }
  if (naver > hub) {
    // 내림 — ② 하락 분류를 그대로.
    if (f.dropBucket === 'IN_RANGE') return apply('네이버를 내림');
    if (f.dropBucket === 'SUSPECT') {
      return f.marketMin != null && hub < f.marketMin
        ? request(`PriceHub 가 타 샵 최저 ${won(f.marketMin)} 보다 낮음, PriceHub 를 고쳐 달라고 요청`)
        : request('PriceHub 값이 타 샵 시세와 맞지 않음, PriceHub 를 고쳐 달라고 요청');
    }
    return check('타 샵 시세가 없어 고를 수 없습니다, ② 하락 검토에서 시세 보고 고르세요');
  }
  // 올림 — D1 안 A(사장 권장): 타 샵 최고가 초과 → 요청, 상한만 넘음 → 확인 필요, 그 밖 → 반영(아침 자동 반영 게이트와 같은 기준).
  if (f.marketMax != null && hub > f.marketMax) return request(`PriceHub 가 타 샵 최고 ${won(f.marketMax)} 보다 높음, 요청`);
  const cap = f.maxRiseRate ?? DEFAULT_MAX_RISE_RATE;
  if ((hub - naver) / naver > cap) return check('큰 상승, 타 샵 시세 보고 고르세요');
  return apply('네이버를 올림');
}

/** PriceHub 확인 중 줄의 글(⏳ 뒤) — "25,000 으로 요청함(11/04 09:10 · 김직원). 확인되면 목록에서 빠집니다"(설계 §2-2 ⑦). */
export function hubCheckingText(r: HubRow): string {
  const p = isHubRequestPending(r) ? r.pendingRequest : null;
  const kind = p?.kind ?? null;
  const at = p?.at ?? r.manualOverrideAt ?? null;
  const when = at && dayjs(at).isValid() ? dayjs(at).format('MM/DD HH:mm') : null;
  const paren = (parts: (string | null | undefined)[]) => {
    const xs = parts.filter(Boolean);
    return xs.length > 0 ? `(${xs.join(' · ')})` : '';
  };
  const tail = '. 확인되면 목록에서 빠집니다';
  if (kind === 'apply_current' || kind === 'manual_request') {
    const price = p?.price;
    return `${price != null ? `${won(price)} 으로 ` : ''}요청함${paren([when, p?.requestedBy])}${tail}`;
  }
  const decided = kind === 'decided' || r.noticeKind === 'decided';
  const price = p?.price ?? (r.kinds.includes('notice') ? r.newPrice : null) ?? r.currentPrice;
  const what = decided ? '판매가 결정' : '네이버에서 바뀜';
  return `${what}${price != null ? ` ${won(price)}` : ''}${paren([when])}${tail}`;
}

/** 'PriceHub 확인 중' 칩의 툴팁 — "PriceHub 확인 중 — 확인되기 전에는 반영 · 요청을 쉽니다(후보 가격 25,000 · 11/04 09:10 · 김직원)". */
export function hubCheckingTooltip(r: HubRow): string {
  const p = isHubRequestPending(r) ? r.pendingRequest : null;
  const price = p?.price ?? (r.kinds.includes('notice') ? r.newPrice : null);
  const at = p?.at ?? r.manualOverrideAt ?? null;
  const parts = [
    price != null ? `후보 가격 ${won(price)}` : null,
    at && dayjs(at).isValid() ? dayjs(at).format('MM/DD HH:mm') : null,
    p?.requestedBy ?? null,
  ].filter(Boolean);
  return `PriceHub 확인 중 — 확인되기 전에는 반영 · 요청을 쉽니다${parts.length > 0 ? `(${parts.join(' · ')})` : ''}`;
}

/**
 * 매장 가격 요청 줄의 안내 재료. 대상 아님 = CT 에 없음 · 네이버 판매가 없음 · 서버/프런트의 요청 불가 사유(고정 · 대상 아닌 게임 · 결과 불명 잠금 등).
 * 확인 중은 대상 아님보다 먼저 본다 — 요청 대기 카드는 서버가 '요청 대기 중' 사유를 함께 주기 때문(그 카드는 확인 중이다).
 * 가격 받기 먼저 = 공통 가격 · 매장 가격 변경만 있는 줄(상승 · 하락 아님)에서 CT 가 받은 값(suggestedPrice)이 PriceHub 가격과 다름.
 */
export function hubRowFacts(r: HubRow): AdviceFacts {
  const hub = hubStorePrice(r);
  const checking = r.cardId != null && isHubChecking(r) ? hubCheckingText(r) : null;
  const excludedReason = r.cardId == null
    ? 'CT 에 없는 상품'
    : checking ? null : (applyCurrentBlockReason(r) ?? null);
  const hubOnly = !r.kinds.includes('rise') && !r.kinds.includes('drop')
    && (r.kinds.includes('hub') || r.kinds.includes('store'));
  return {
    naver: r.currentPrice, hub, excludedReason, checking,
    priceHeld: r.priceHeld === true,
    // CT 가 받아 둔 PriceHub 값(hubPrice, 옛 응답은 수정가)이 PriceHub 가 지금 주는 값과 다르면 아직 안 받은 것.
    fetchFirst: hubOnly && (r.hubPrice ?? r.suggestedPrice) != null && hub != null && (r.hubPrice ?? r.suggestedPrice) !== hub ? { at: r.at } : null,
    dropBucket: r.dropBucket ?? null,
    marketMin: r.marketMin ?? null,
    marketMax: r.marketMax ?? null,
  };
}

/** 매장 가격 요청 줄의 안내(rowAdvice(hubRowFacts(r))). 두 값이 같으면 null(목록에서 빠지는 줄). */
export const hubRowAdvice = (r: HubRow): RowAdvice | null => rowAdvice(hubRowFacts(r));

// ─── 보기 칩 셋(설계 2026-11-04 §3 — 탭 3 → 칩 셋) ───────────────────────────────────────────────────────

/** 매장 가격 요청 표의 보기 — 할 일(반영 · 요청, 체크) · PriceHub 확인 중(글로) · 대상 아님(사유를 글로). 이름은 옛 탭 값 그대로(저장 칩 · 주소 호환). */
export type HubStatusTab = 'available' | 'pending' | 'blocked';

export const HUB_STATUS_LABEL: Record<HubStatusTab, string> = {
  available: '할 일',
  pending:   'PriceHub 확인 중',
  blocked:   '대상 아님',
};

/** 줄의 보기(안내 종류에서 — checking → 확인 중, excluded → 대상 아님, 나머지 → 할 일). 두 값이 같은 줄(목록에 없음)은 대상 아님으로 둔다. */
export function hubRowStatus(r: HubRow): HubStatusTab {
  const a = hubRowAdvice(r);
  if (!a || a.kind === 'excluded') return 'blocked';
  if (a.kind === 'checking') return 'pending';
  return 'available';
}

/** 대상 아님 줄의 사유(칸에 글로). */
export const hubBlockedText = (r: HubRow): string => {
  const a = hubRowAdvice(r);
  return a?.kind === 'excluded' ? a.text : (a ? '' : '네이버 판매가와 PriceHub 가격이 같습니다');
};

/**
 * 이 줄을 체크(요청)할 수 없는 사유(체크할 수 있으면 undefined) — '할 일' 이 아닌 줄(대상 아님 · 확인 중) · 지금 요청을 보낼 수 없는 줄.
 */
export function hubRowBlockReason(r: HubRow): string | undefined {
  if (r.cardId == null) return HUB_NO_CARD_TOOLTIP;
  const s = hubRowStatus(r);
  if (s === 'pending') return hubCheckingTooltip(r);
  if (s === 'blocked') return hubBlockedText(r) || '대상 아님';
  return applyCurrentBlockReason(r);
}
